import React from 'react';
import { SectionTitle } from '../SectionTitle';
import OAEGraph from '../../oae/OAEGraph';
import {
  freqsFor, pointAt, overallResult, bilateralSummary, normaliseOAE, fmtFreq,
} from '../../oae/oaeLogic';

/**
 * OAEReportSection — rich OAE renderer for the standard hearing
 * report (both in-app preview and the saved-snapshot viewer).
 *
 * Falls back to a compact "no data" line when the audiologist hasn't
 * populated any OAE values yet — matches the behaviour of every other
 * report section.
 */
export const OAEReportSection = ({ oae }) => {
  const state = normaliseOAE(oae);
  const freqs = freqsFor(state.test_type);
  const rightHas = Object.keys(state.right.points || {}).length > 0;
  const leftHas  = Object.keys(state.left.points  || {}).length > 0;
  const anyData  = rightHas || leftHas
    || state.probe_type || state.stimulus || state.equipment
    || state.comments || state.recommendations;
  if (!anyData) return null;

  const rightResult = overallResult(state.right, freqs);
  const leftResult  = overallResult(state.left, freqs);
  const bilateral   = bilateralSummary(rightResult, leftResult);

  const MiniEar = ({ ear, result }) => {
    const label = ear === 'right' ? 'Right' : 'Left';
    const badgeCls = result === 'Pass'
      ? 'bg-emerald-600 text-white'
      : result === 'Refer'
        ? 'bg-red-600 text-white'
        : 'bg-slate-400 text-white';
    return (
      <div className="border border-slate-300 rounded">
        <div className="flex items-center justify-between px-1.5 py-0.5 bg-slate-50 border-b border-slate-300">
          <span className={`text-[10px] font-bold ${ear === 'right' ? 'text-red-700' : 'text-blue-700'}`}>{label} Ear</span>
          <span className={`text-[9px] font-bold uppercase px-1.5 py-0 rounded ${badgeCls}`}>{result || 'NO DATA'}</span>
        </div>
        <div className="p-1">
          <OAEGraph
            ear={ear}
            earData={state[ear]}
            freqs={freqs}
            plotKind={null}
            readOnly
            passResult={result}
            width={300}
            height={140}
          />
        </div>
        <table className="w-full text-[9px] border-t border-slate-300">
          <thead className="bg-slate-50">
            <tr>
              <th className="border border-slate-300 px-0.5">Freq</th>
              <th className="border border-slate-300 px-0.5">OAE</th>
              <th className="border border-slate-300 px-0.5">Noise</th>
              <th className="border border-slate-300 px-0.5">SNR</th>
              <th className="border border-slate-300 px-0.5">Res</th>
            </tr>
          </thead>
          <tbody>
            {freqs.map((f) => {
              const { oae: o, noise: n, snr, result: r } = pointAt(state[ear], f);
              return (
                <tr key={f}>
                  <td className="border border-slate-300 px-0.5 text-center font-mono">{fmtFreq(f)}k</td>
                  <td className="border border-slate-300 px-0.5 text-center font-mono">{o ?? '—'}</td>
                  <td className="border border-slate-300 px-0.5 text-center font-mono">{n ?? '—'}</td>
                  <td className="border border-slate-300 px-0.5 text-center font-mono">{snr === null ? '—' : snr.toFixed(1)}</td>
                  <td className={`border border-slate-300 px-0.5 text-center font-bold ${
                    r === 'Pass' ? 'text-emerald-700' : r === 'Refer' ? 'text-red-700' : 'text-slate-400'
                  }`}>{r ? r[0] : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div data-testid="oae-report-section">
      <SectionTitle>
        Otoacoustic Emissions · {state.test_type} · {state.purpose}
      </SectionTitle>
      <div className="grid grid-cols-2 gap-2 mb-1">
        <MiniEar ear="right" result={rightResult} />
        <MiniEar ear="left"  result={leftResult} />
      </div>
      {bilateral && (
        <div
          data-testid="oae-report-section-bilateral"
          className={`text-center text-[10px] font-bold py-0.5 mb-1 rounded ${
            bilateral === 'Bilateral Pass'
              ? 'bg-emerald-100 text-emerald-800 border border-emerald-400'
              : bilateral === 'Bilateral Refer'
                ? 'bg-red-100 text-red-800 border border-red-400'
                : 'bg-amber-100 text-amber-800 border border-amber-400'
          }`}
        >
          Summary: {bilateral}
        </div>
      )}
      {(state.probe_type || state.stimulus || state.equipment || state.environment) && (
        <div className="text-[9px] text-slate-700 grid grid-cols-4 gap-x-3 mb-1">
          {state.probe_type && <div><b>Probe:</b> {state.probe_type}</div>}
          {state.stimulus && <div><b>Stimulus:</b> {state.stimulus}</div>}
          {state.equipment && <div><b>Equipment:</b> {state.equipment}</div>}
          {state.environment && <div><b>Env:</b> {state.environment}</div>}
        </div>
      )}
      {state.comments && (
        <div className="text-[10px] text-slate-800 mb-0.5">
          <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Comments:</span>
          <span className="whitespace-pre-wrap">{state.comments}</span>
        </div>
      )}
      {state.interpretation && (
        <div className="text-[10px] text-slate-800 mb-0.5">
          <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Interpretation:</span>
          <span className="whitespace-pre-wrap">{state.interpretation}</span>
        </div>
      )}
      {state.recommendations && (
        <div className="text-[10px] text-slate-800">
          <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Recommendations:</span>
          <span className="whitespace-pre-wrap">{state.recommendations}</span>
        </div>
      )}
    </div>
  );
};

export default OAEReportSection;
